/**
 * Helper functions for Latest Session Feedback page
 * Transforms BE response data to display format
 */

import {
  IndividualSessionFeedbackData,
  TopStrength,
  ImprovementIdea
} from '../../../../types/IndividualSessionFeedbackTypes'
import { NormalizedDatapointDirection } from '../../../../types/SkimmerDataTypes'
import { FeedbackStatusLevel } from '../../../../../../pure-js/enums/FeedbackLevel'
import { TFunction } from 'i18next'
import { INDIVIDUAL_PROFILE_LABELS } from '../../../feedbackGenerator/utils/individualProfileMapping'
import { generateIndividualProfileDescription } from '../../../feedbackGenerator/utils/profileDescriptionGenerator'
import type { ProfileIndicator } from '../../../feedbackGenerator/components/profileIndicatorTypes'
import { getVariableLabel } from '../../../../shared/utils/variableTranslationHelpers'
import {
  resolveStrengthVariableForLocales,
  resolveWeaknessVariableForLocales
} from './resolveStrengthVariableForLocales'
import { readFeedbackStatusLevel } from './myProfileHelpers'
import type { User } from '../../../../../../pure-js/types/User'

/**
 * Map improvement idea recommendation direction to skimmer direction
 */
export function mapImprovementDirectionToSkimmer(
  direction: 'increase' | 'decrease' | 'balance'
): NormalizedDatapointDirection {
  switch (direction) {
    case 'increase':
      return NormalizedDatapointDirection.MORE
    case 'decrease':
      return NormalizedDatapointDirection.LESS
    case 'balance':
      return NormalizedDatapointDirection.BALANCED
    default:
      return NormalizedDatapointDirection.MORE
  }
}

/**
 * Transform top strengths to profile pill format
 * Returns first 2 strengths for display
 *
 * @param topStrengths - Array of top strengths from BE
 * @returns Array of strength objects for profile pills
 */
export function transformTopStrengthsToProfilePills(
  topStrengths: TopStrength[]
): Array<{ variable: string; normalizedScore: number; value?: number }> {
  if (!topStrengths || topStrengths.length === 0) {
    return []
  }

  // Return first 2 strengths (or all if less than 2)
  return topStrengths.slice(0, 2)
}

/**
 * Map improvement idea to visualization format
 * Prepares improvement idea data for ImprovementIdeaVisualization component
 *
 * @param improvementIdea - Improvement idea from BE
 * @param t - Translation function
 * @returns Object with description and recommendation for visualization
 */
// Backend sends 'increase' | 'decrease' | 'balance' (SkimmerSwimmerHandler); balance has no potential* text.
const POTENTIAL_FIELD_BY_DIRECTION: Record<string, 'potentialIncrease' | 'potentialDecrease' | undefined> = {
  increase: 'potentialIncrease',
  decrease: 'potentialDecrease'
}
const DIRECTION_GERUNDS: Record<string, string> = {
  increase: 'increasing',
  decrease: 'decreasing',
  balance: 'balancing'
}

export function mapImprovementIdeaToVisualization(
  improvementIdea: ImprovementIdea,
  t: TFunction
): {
  description: string
  recommendation: string
} {
  // BE casing diverges from the locale keys for the *Preplanning metrics; resolve once for label + text.
  const localeVariable = resolveWeaknessVariableForLocales(improvementIdea.variable)
  const improvementIdeaLabel = getVariableLabel(localeVariable, t, {
    context: 'weaknesses',
    type: 'label'
  })

  // Format recommendation pill text
  const recommendationDirection = improvementIdea.recommendationDirection
  const translationKey = `gamer.feedback.improvementIdea.actions.${recommendationDirection}`
  const fallbackPrefix = recommendationDirection.charAt(0).toUpperCase() + recommendationDirection.slice(1)
  const recommendation = t(translationKey, {
    label: improvementIdeaLabel,
    defaultValue: `${fallbackPrefix} ${improvementIdeaLabel}`
  })

  // The weakness entries carry no `description` field — the direction-matched `potential*` text IS the
  // written recommendation ("Consider increasing …"). The old `.description` lookup matched nothing, so
  // its `${direction}ing` fallback ("increaseing") rendered on every card.
  const potentialField = POTENTIAL_FIELD_BY_DIRECTION[recommendationDirection]
  const fallbackSentence = `Consider ${DIRECTION_GERUNDS[recommendationDirection] ?? recommendationDirection} ${improvementIdeaLabel}`
  const description = potentialField
    ? t(`player.weaknesses.${localeVariable}.${potentialField}`, {
        ns: 'feedbackTexts',
        defaultValue: fallbackSentence
      })
    : fallbackSentence

  return {
    description,
    recommendation
  }
}

/**
 * Format session time from date string
 *
 * @param dateString - ISO date string or Date object
 * @param _t - Translation function (reserved for future use)
 * @returns Formatted date/time string (e.g., "Thursday 20th of November, 09:30")
 */
export function formatSessionTime(dateString: string | Date | undefined, _t: TFunction): string {
  if (!dateString) {
    return ''
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString

    if (isNaN(date.getTime())) {
      return ''
    }

    // Format: "Thursday 20th of November, 09:30"
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' })
    const day = date.getDate()
    const monthName = date.toLocaleDateString('en-US', { month: 'long' })
    const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })

    // Get ordinal suffix for day
    const ordinal = getOrdinalSuffix(day)

    return `${dayName} ${day}${ordinal} of ${monthName}, ${time}`
  } catch (error) {
    console.error('Error formatting session time:', error)
    return ''
  }
}

/**
 * Get ordinal suffix for day number
 * @param day - Day number (1-31)
 * @returns Ordinal suffix ('st', 'nd', 'rd', or 'th')
 */
function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) {
    return 'th'
  }

  switch (day % 10) {
    case 1: {
      return 'st'
    }
    case 2: {
      return 'nd'
    }
    case 3: {
      return 'rd'
    }
    default: {
      return 'th'
    }
  }
}

/**
 * Generate profile title from top strengths
 * Creates a combined title like "Disciplined Moderator" from top 2 strengths
 *
 * @param topStrengths - Array of top strengths
 * @returns Combined profile title
 */
export function generateProfileTitleFromStrengths(topStrengths: TopStrength[]): string {
  if (!topStrengths || topStrengths.length === 0) {
    return 'Your Profile'
  }

  if (topStrengths.length === 1) {
    const label = INDIVIDUAL_PROFILE_LABELS[topStrengths[0].variable]?.topStrength || 'Strong'
    return label
  }

  // Use first two strengths to generate combined title
  const topLabel = INDIVIDUAL_PROFILE_LABELS[topStrengths[0].variable]?.topStrength || 'Strong'
  const secondLabel = INDIVIDUAL_PROFILE_LABELS[topStrengths[1].variable]?.secondStrength || 'Performer'

  return `${topLabel} ${secondLabel}`
}

/**
 * Generate profile description from top strengths
 * Uses the same logic as generateIndividualProfileDescription
 *
 * @param topStrengths - Array of top strengths
 * @param t - Translation function
 * @returns Profile description text
 */
export function generateProfileDescriptionFromStrengths(topStrengths: TopStrength[], t: TFunction): string {
  if (!topStrengths || topStrengths.length < 2) {
    return t('gamer.feedback.profile.description', 'Your top strengths from this session')
  }

  // TopStrength is compatible with StrengthProfile (both have variable and normalizedScore)
  return generateIndividualProfileDescription(
    topStrengths[0] as any, // Type assertion - TopStrength is compatible with StrengthProfile
    topStrengths[1] as any,
    t
  )
}

/** The words shown for each status; a good strength reads as outstanding. */
const PROFILE_STATUS_LABELS: Record<FeedbackStatusLevel, { key: string; defaultValue: string }> = {
  [FeedbackStatusLevel.EXCELLENT]: { key: 'gamer.feedback.profile.status.excellent', defaultValue: 'Excellent' },
  [FeedbackStatusLevel.GOOD]: { key: 'gamer.feedback.profile.status.outstanding', defaultValue: 'Outstanding' },
  [FeedbackStatusLevel.NEEDS_WORK]: { key: 'gamer.feedback.profile.status.needsWork', defaultValue: 'Needs Work' }
}

/**
 * Map a strength's status level to its status text
 *
 * @param statusLevel - The status level from the answer
 * @param t - Translation function
 * @returns Object with status text and level
 */
export function mapLevelToStatus(
  statusLevel: FeedbackStatusLevel | undefined,
  t: TFunction
): { status: string; level: FeedbackStatusLevel } {
  const level = readFeedbackStatusLevel(statusLevel)
  const label = PROFILE_STATUS_LABELS[level]

  return { status: t(label.key, { defaultValue: label.defaultValue }), level }
}

/**
 * Transform top strengths to ProfileIndicator format for ProfileCardWithIndicators
 *
 * @param topStrengths - Array of top strengths from BE
 * @param t - Translation function
 * @returns Array of ProfileIndicator objects
 */
export function transformTopStrengthsToIndicators(topStrengths: TopStrength[], t: TFunction): ProfileIndicator[] {
  if (!topStrengths || topStrengths.length === 0) {
    return []
  }

  // Take first 2 strengths and transform to indicators
  return topStrengths.slice(0, 2).map((strength) => {
    const { status, level } = mapLevelToStatus(strength.statusLevel, t)
    const resolvedVariable = resolveStrengthVariableForLocales(strength.variable)
    const label = getVariableLabel(resolvedVariable, t, { context: 'strengths', type: 'label' })
    const tooltipResolved = getVariableLabel(resolvedVariable, t, { context: 'strengths', type: 'tooltip' })
    const trimmedLabel = label.trim()
    const trimmedTooltip = tooltipResolved.trim()
    /** Prefer distinct `feedbackTexts` tooltip; otherwise use label so hover/popover never disappears (see plan safe fallback). */
    const tooltip =
      trimmedTooltip !== '' && trimmedTooltip !== trimmedLabel
        ? trimmedTooltip
        : trimmedLabel !== ''
          ? trimmedLabel
          : undefined

    return {
      label,
      status,
      level,
      tooltip
    }
  })
}

/**
 * Check if feedback data has all required fields
 *
 * @param data - Feedback data to validate
 * @returns True if data is valid
 */
export function isValidFeedbackData(
  data: IndividualSessionFeedbackData | undefined
): data is IndividualSessionFeedbackData {
  if (!data) {
    return false
  }

  return (
    Array.isArray(data.topStrengths) &&
    typeof data.improvementIdea === 'object' &&
    typeof data.communicatedEmotion === 'object' &&
    typeof data.communicatedEmotion.dominantEmotion === 'string'
  )
}

/** Feedback of their latest game is ready and they have not uncovered it yet. */
export const hasUnreadLatestFeedback = (
  feedback: Pick<IndividualSessionFeedbackData, 'sessionId' | 'feedbackReady'> | undefined,
  user: Pick<User, 'tasks'> | undefined
): boolean => {
  const readSessionId = user?.tasks?.latestSessionFeedback?.sessionId
  return Boolean(feedback?.feedbackReady && feedback.sessionId && feedback.sessionId !== readSessionId)
}
